import { useEffect, useState } from 'react';
import { Route } from '../../../enums';
import { useDispatch, useSelector } from 'react-redux';
import {
  cartSlice,
  CartState,
  customerSlice,
  postGenerateExpressCheckoutToken,
  StoreState,
  useClearCartEffect,
  useFindCartByClientIdEffect,
} from '../../../services';
import { useStripe } from '@stripe/react-stripe-js';
import { currencyLabel, paypalUrlUtil, windowOpen } from '../../../utils';
import { PaymentMethods } from '../../../data/subscription/payment-methods';
import { usePaypalCheckoutCompletePayment } from '../../../forms';

export const usePatientPaymentDetailsStepEffect = () => {
  const { customer, converted } = useSelector(
    (state: StoreState) => state.customers.customer
  );
  const cartState = useSelector(
    (state: StoreState) => state.orders.cart
  ) as CartState;
  const { cart, paypal, paymentGateway } = cartState;
  const [paymentMethod, setPaymentMethod] = useState(
    paymentGateway ? paymentGateway : PaymentMethods.STRIPE
  );
  const { findCartByClientId } = useFindCartByClientIdEffect();
  const {
    paypalError,
    paypalProcessing,
    paypalComplete,
    submitPaypalComplete,
  } = usePaypalCheckoutCompletePayment(cart ? cart?.clientId : '');
  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [paymentRequestType, setPaymentRequestType] = useState({
    applePay: false,
    googlePay: false,
  });
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const hostname =
    typeof window !== 'undefined' && window.location.origin
      ? window.location.origin
      : '';
  let regionCode = cart?.region.toString();
  switch (regionCode) {
    case 'UK':
      regionCode = 'GB';
      break;
    case 'EU':
      regionCode = customer?.shippingAddress?.country;
      break;
  }
  const paymentRequestOption = {
    country: regionCode as string,
    currency: currencyLabel(),
    total: {
      label: 'Payment due',
      amount: cart?.grandTotalPrice as number,
    },
    requestPayerName: true,
    requestPayerEmail: true,
  };
  const total = cart?.grandTotalPrice as number;
  const dispatch = useDispatch();
  const { clearCart } = useClearCartEffect(Route.PATIENT_REGISTER);

  useEffect(() => {
    if (cart && cart.clientId) {
      findCartByClientId(cart.clientId, true);
    }

    if (stripe) {
      const pr = stripe.paymentRequest(paymentRequestOption);
      pr.canMakePayment().then((result) => {
        if (result) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setPaymentRequestType(result);
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          setPaymentRequest(pr);
        }
      });
    }
  }, [stripe]);

  useEffect(() => {
    dispatch(cartSlice.actions.setPaymentGateway(paymentMethod));
  }, [paymentMethod]);

  useEffect(() => {
    if (converted) {
      clearCart();
    }
    if (paypalComplete) {
      dispatch(customerSlice.actions.checkout(cartState));
    }
  }, [paypalComplete, paymentMethod, converted, cartState]);

  const doPayPalSetup = () => {
    setPaymentMethod(PaymentMethods.PAYPAL);
    setProcessing(true);

    const generatePaypalToken = postGenerateExpressCheckoutToken({
      orderTotal: total > 0 ? total : 100,
      currency: currencyLabel(true),
      returnUrl: hostname + Route.CHECKOUT_PAYPAL_RETURN_URL,
      cancelUrl: hostname + Route.CHECKOUT_PAYPAL_CANCEL_URL,
      address: {
        name: `${customer!.firstName} ${customer!.lastName}`,
        line1: customer!.billingAddress!.line1,
        line2: customer!.billingAddress!.line2 || undefined,
        city: customer!.billingAddress!.city,
        state: customer!.billingAddress!.state,
        country: customer!.billingAddress!.country,
        postalCode: customer!.billingAddress!.postalCode,
      },
    });

    generatePaypalToken.then((token: any) => {
      if (token) {
        if (!token.dataResponse?.success) {
          setError(token.dataResponse?.data.error.message);
        } else {
          const generatedPaypalToken = token.dataResponse.data.token;
          if (generatedPaypalToken != '') {
            dispatch(cartSlice.actions.setPaypalToken(generatedPaypalToken));
            windowOpen(paypalUrlUtil() + generatedPaypalToken);
          }
        }
      }
    });
  };

  return {
    customer,
    paymentMethod,
    paymentRequest,
    paymentRequestOption,
    paymentRequestType,
    setPaymentMethod,
    doPayPalSetup,
    paymentGateway,
    paypal,
    error,
    processing,
    submitPaypalComplete,
    paypalComplete,
    paypalError,
    paypalProcessing,
  };
};
